/**
 * Purchase orders, To buy (BLU-1268, plan 90 section 5.5). The lists for the
 * person who buys, at one owned floor:
 *
 *   Order and wait       (BLU-1506) blanks an Order and wait supplier
 *                        delivers: the next order to send, the boxes on the
 *                        way, Send and Arrived. See `OrderAndWaitSection`.
 *   Buy today            open just-in-time POs (a batch wrote each one): open
 *                        it, or press Bought as listed when the shop had
 *                        everything. A "No supplier" PO waits for a supplier.
 *   Below keep at least  stocked blanks under their keep-at-least level,
 *                        grouped by supplier, with an editable To buy and one
 *                        Make draft PO per supplier. Drafts count as on order,
 *                        so pressing it again makes nothing new.
 *
 * The floor is picked in the page's action bar (the PO list's own control
 * home, DESIGN.md section 3); this tab only renders. Below keep at least is the
 * floor owner's list: anyone else gets FORBIDDEN from the server, shown as a
 * plain "only the floor's owner" state, never as an error.
 *
 * One table for the second list, a `<tbody>` per supplier with a
 * `scope="rowgroup"` header (DESIGN.md section 12.3), so a screen reader hears
 * which supplier a row belongs to. On a phone the less needed columns hide;
 * the table never scrolls sideways.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCheck, ClipboardList, FilePlus2, Loader2, Lock, PackageCheck } from 'lucide-react';
import type { ToBuyRow, ToBuySupplierGroup } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { useRole } from '@/hooks/use-role';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { formatDay, useDateFnsLocale } from '@/lib/format-dates';
import { normalizeNumericText } from '@/lib/parse-kg';
import { cn } from '@/lib/utils';
import type { PurchaseOrder } from '@/features/purchase-orders';
import {
  BUY_TODAY_LIMIT,
  useBuyToday,
  useCompleteAsListed,
  useMakeDrafts,
  useToBuyList,
} from '../hooks/useToBuy';
import { jitRowName } from '../lib/jit-lines';
import { CompleteJustInTimeDialog } from './CompleteJustInTimeDialog';
import { OrderAndWaitSection } from './OrderAndWaitSection';

/** createDraftsFromToBuy's per-line cap (TO_BUY_MAX_QTY). */
const MAX_TO_BUY = 999_999;

interface ToBuyTabProps {
  /** The owned floor picked in the action bar; null while none is known. */
  locationId: string | null;
  locationName: string | null;
  /** The floor's IANA timezone (Order and wait's "Send by" is floor time); the browser's when absent. */
  locationTimezone?: string | null;
  /** The PO list URL (this tab included), for each PO link's back button. */
  ordersReturnTo: string;
  /** Show every open just-in-time PO in the Orders tab (more than Buy today lists). */
  onShowAllOpen: () => void;
}

export function ToBuyTab({
  locationId,
  locationName,
  locationTimezone = null,
  ordersReturnTo,
  onShowAllOpen,
}: ToBuyTabProps) {
  const { t } = useTranslation('orders');

  if (!locationId) {
    return (
      <EmptyState
        icon={PackageCheck}
        title={t('toBuy.noFloor')}
        description={t('toBuy.noFloorBody')}
        className="mt-6"
      />
    );
  }

  return (
    <div className="mt-6 space-y-8">
      <OrderAndWaitSection
        locationId={locationId}
        locationName={locationName}
        locationTimezone={locationTimezone}
        ordersReturnTo={ordersReturnTo}
      />
      <BuyTodaySection
        locationId={locationId}
        ordersReturnTo={ordersReturnTo}
        onShowAllOpen={onShowAllOpen}
      />
      <BelowKeepSection locationId={locationId} locationName={locationName} />
    </div>
  );
}

// ── Buy today ────────────────────────────────────────────────────────────────

function BuyTodaySection({
  locationId,
  ordersReturnTo,
  onShowAllOpen,
}: {
  locationId: string;
  ordersReturnTo: string;
  onShowAllOpen: () => void;
}) {
  const { t } = useTranslation('orders');
  const buyToday = useBuyToday(locationId);
  const { completeAsListed, pendingOrderId } = useCompleteAsListed();
  const [confirming, setConfirming] = useState<PurchaseOrder | null>(null);

  return (
    <section aria-labelledby="to-buy-today" className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 id="to-buy-today" className="text-base font-semibold">
            {t('toBuy.buyToday')}
            {buyToday.total > 0 && (
              <span className="ml-2 text-sm font-normal tabular-nums text-muted-foreground">{buyToday.total}</span>
            )}
          </h2>
          <p className="text-sm text-muted-foreground">{t('toBuy.buyTodayHelp')}</p>
        </div>
        {buyToday.total > buyToday.orders.length && (
          <Button variant="link" className="h-auto p-0 text-sm" onClick={onShowAllOpen}>
            {t('toBuy.showAllOpen', { count: buyToday.total })}
          </Button>
        )}
      </div>

      {buyToday.loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" role="status" aria-label={t('toBuy.loading')}>
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} className="h-36 rounded-lg" />
          ))}
        </div>
      ) : buyToday.failed ? (
        <SectionError
          title={t('toBuy.buyTodayFailed')}
          onRetry={buyToday.retry}
          retryLabel={t('jit.retry')}
          retrying={buyToday.retrying}
        />
      ) : buyToday.orders.length === 0 ? (
        <EmptyState
          icon={CheckCheck}
          size="sm"
          title={t('toBuy.buyTodayEmpty')}
          description={t('toBuy.buyTodayEmptyBody')}
        />
      ) : (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {buyToday.orders.map((order) => (
            <BuyTodayCard
              key={order.id}
              order={order}
              ordersReturnTo={ordersReturnTo}
              pending={pendingOrderId === order.id}
              onBoughtAsListed={() => setConfirming(order)}
            />
          ))}
        </ul>
      )}
      {buyToday.orders.length > 0 && buyToday.total > BUY_TODAY_LIMIT && (
        <NoteLine as="p">{t('toBuy.buyTodayCapped', { count: BUY_TODAY_LIMIT })}</NoteLine>
      )}

      <CompleteJustInTimeDialog
        open={confirming !== null}
        onOpenChange={(open) => (open ? undefined : setConfirming(null))}
        asListed
        poNumber={confirming?.po_number ?? ''}
        pending={confirming !== null && pendingOrderId === confirming.id}
        onConfirm={() => (confirming ? completeAsListed(confirming) : Promise.resolve(false))}
      />
    </section>
  );
}

function BuyTodayCard({
  order,
  ordersReturnTo,
  pending,
  onBoughtAsListed,
}: {
  order: PurchaseOrder;
  ordersReturnTo: string;
  pending: boolean;
  onBoughtAsListed: () => void;
}) {
  const { t } = useTranslation('orders');
  const { canManagePurchaseOrders } = useRole();
  const { format } = useCurrencyFormatter();
  const dateLocale = useDateFnsLocale();
  const items = order.items ?? [];
  const units = items.reduce((sum, item) => sum + item.ordered_quantity, 0);
  const noSupplier = !order.supplier_id;
  const reasonId = `buy-today-${order.id}-reason`;

  return (
    <li>
      <Card className="flex h-full flex-col">
        <CardContent className="flex flex-1 flex-col gap-3 p-4">
          <div className="min-w-0">
            <Link
              to={`/orders/${order.id}`}
              state={{ ordersReturnTo }}
              className="font-medium text-primary-strong underline-offset-4 hover:underline"
            >
              {order.po_number}
            </Link>
            <p className={cn('truncate text-sm', noSupplier ? 'text-amber-700' : 'text-foreground')}>
              {order.supplier_name || t('jit.noSupplier')}
            </p>
          </div>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
            <dt className="text-muted-foreground">{t('toBuy.card.lines')}</dt>
            <dd className="tabular-nums">{t('toBuy.card.linesValue', { count: items.length, units })}</dd>
            <dt className="text-muted-foreground">{t('toBuy.card.estTotal')}</dt>
            <dd className="tabular-nums">
              {order.total_cost ? format(order.total_cost, order.currency) : <span className="text-muted-foreground">-</span>}
            </dd>
            <dt className="text-muted-foreground">{t('toBuy.card.created')}</dt>
            <dd>{formatDay(order.created_at, dateLocale)}</dd>
          </dl>
          <div className="mt-auto flex flex-wrap gap-2">
            <Button asChild variant="outline" className="h-10 flex-1 text-xs sm:h-8">
              <Link to={`/orders/${order.id}`} state={{ ordersReturnTo }}>
                {t('toBuy.card.open')}
              </Link>
            </Button>
            {canManagePurchaseOrders() && (
              <Button
                className="h-10 flex-1 gap-1.5 bg-threadheads-purple text-xs text-white hover:bg-threadheads-purple/90 sm:h-8"
                onClick={onBoughtAsListed}
                disabled={noSupplier || pending}
                aria-describedby={noSupplier ? reasonId : undefined}
              >
                {pending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                ) : (
                  <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {t('jit.boughtAsListed')}
              </Button>
            )}
          </div>
          {noSupplier && canManagePurchaseOrders() && (
            <NoteLine as="p" id={reasonId} className="text-xs">
              {t('toBuy.card.needsSupplier')}
            </NoteLine>
          )}
        </CardContent>
      </Card>
    </li>
  );
}

// ── Below keep at least ──────────────────────────────────────────────────────

const groupKey = (group: ToBuySupplierGroup): string => group.supplierId ?? 'none';

type QtyParse = { ok: true; value: number } | { ok: false };

const parseToBuy = (text: string): QtyParse => {
  const s = normalizeNumericText(text);
  if (s === '') return { ok: true, value: 0 };
  if (!/^\d+$/.test(s)) return { ok: false };
  const value = Number(s);
  return value <= MAX_TO_BUY ? { ok: true, value } : { ok: false };
};

function BelowKeepSection({ locationId, locationName }: { locationId: string; locationName: string | null }) {
  const { t } = useTranslation('orders');
  const list = useToBuyList(locationId);
  const { canManagePurchaseOrders } = useRole();
  const { makeDrafts, pendingSupplierKey } = useMakeDrafts();
  // Raw text per SKU id; only rows the operator touched (DESIGN.md section 12.1).
  const [edits, setEdits] = useState<Record<string, string>>({});
  const canManage = canManagePurchaseOrders();
  const floor = locationName ?? t('toBuy.thisFloor');

  const groups = list.data?.groups ?? [];

  const textFor = (row: ToBuyRow) => edits[row.skuId] ?? String(row.toBuy);

  const handleMake = async (group: ToBuySupplierGroup) => {
    if (!group.supplierId) return;
    const lines = group.rows
      .map((row) => ({ skuId: row.skuId, parsed: parseToBuy(textFor(row)) }))
      .filter((line): line is { skuId: string; parsed: { ok: true; value: number } } => line.parsed.ok && line.parsed.value > 0)
      .map((line) => ({ skuId: line.skuId, qty: line.parsed.value }));
    if (lines.length === 0) return;
    const result = await makeDrafts({
      locationId,
      supplierKey: groupKey(group),
      supplierName: group.supplierName ?? '',
      lines,
    });
    if (result) {
      // The list re-reads with the drafts counted as on order; drop this group's edits.
      setEdits((current) => {
        const next = { ...current };
        for (const row of group.rows) delete next[row.skuId];
        return next;
      });
    }
  };

  return (
    <section aria-labelledby="to-buy-below-keep" className="space-y-3">
      <div>
        <h2 id="to-buy-below-keep" className="text-base font-semibold">
          {t('toBuy.belowKeep')}
          {list.data && list.data.total > 0 && (
            <span className="ml-2 text-sm font-normal tabular-nums text-muted-foreground">{list.data.total}</span>
          )}
        </h2>
        <p className="text-sm text-muted-foreground">{t('toBuy.belowKeepHelp')}</p>
      </div>

      {list.forbidden ? (
        <EmptyState icon={Lock} size="sm" title={t('toBuy.ownerOnly')} description={t('toBuy.ownerOnlyBody', { floor })} />
      ) : list.loading ? (
        <div className="space-y-2" role="status" aria-label={t('toBuy.loading')}>
          {[0, 1, 2, 3].map((key) => (
            <Skeleton key={key} className="h-10 rounded-md" />
          ))}
        </div>
      ) : list.failed ? (
        <SectionError title={t('toBuy.belowKeepFailed')} onRetry={list.retry} retryLabel={t('jit.retry')} retrying={list.retrying} />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          size="sm"
          title={t('toBuy.belowKeepEmpty', { floor })}
          description={t('toBuy.belowKeepEmptyBody')}
        />
      ) : (
        <>
          {list.data?.truncated && (
            <NoteLine as="p" tone="warning">
              {t('toBuy.truncated', { count: groups.reduce((sum, group) => sum + group.rows.length, 0) })}
            </NoteLine>
          )}
          <div className="rounded-lg border bg-card">
            <table className="w-full table-fixed text-sm">
              <caption className="sr-only">{t('toBuy.tableCaption', { floor })}</caption>
              <thead>
                <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th scope="col" className="px-3 py-2 text-left font-medium">{t('toBuy.col.blank')}</th>
                  <th scope="col" className="hidden w-36 px-3 py-2 text-left font-medium md:table-cell">{t('toBuy.col.supplierSku')}</th>
                  <th scope="col" className="w-20 px-3 py-2 text-right font-medium">{t('toBuy.col.shelf')}</th>
                  <th scope="col" className="hidden w-24 px-3 py-2 text-right font-medium sm:table-cell">{t('toBuy.col.keep')}</th>
                  <th scope="col" className="hidden w-24 px-3 py-2 text-right font-medium sm:table-cell">{t('toBuy.col.onOrder')}</th>
                  <th scope="col" className="w-24 px-3 py-2 text-right font-medium">{t('toBuy.col.toBuy')}</th>
                </tr>
              </thead>
              {groups.map((group) => (
                <SupplierGroup
                  key={groupKey(group)}
                  group={group}
                  canManage={canManage}
                  textFor={textFor}
                  onEdit={(skuId, text) => setEdits((current) => ({ ...current, [skuId]: text }))}
                  pending={pendingSupplierKey === groupKey(group)}
                  busy={pendingSupplierKey !== null}
                  onMake={() => void handleMake(group)}
                />
              ))}
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function SupplierGroup({
  group,
  canManage,
  textFor,
  onEdit,
  pending,
  busy,
  onMake,
}: {
  group: ToBuySupplierGroup;
  canManage: boolean;
  textFor: (row: ToBuyRow) => string;
  onEdit: (skuId: string, text: string) => void;
  pending: boolean;
  busy: boolean;
  onMake: () => void;
}) {
  const { t } = useTranslation('orders');
  const parsed = group.rows.map((row) => parseToBuy(textFor(row)));
  const invalid = parsed.filter((p) => !p.ok).length;
  const lineCount = parsed.filter((p) => p.ok && p.value > 0).length;
  const name = group.supplierName ?? t('jit.noSupplier');
  const headerId = `to-buy-group-${group.supplierId ?? 'none'}`;

  return (
    <tbody className="border-b last:border-b-0">
      <tr className="bg-muted/40">
        <th scope="rowgroup" colSpan={6} id={headerId} className="px-3 py-2 text-left font-normal">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className={cn('text-sm font-semibold', !group.supplierId && 'text-amber-700')}>{name}</span>
            {group.supplierId ? (
              canManage && (
                <Button
                  size="sm"
                  className="h-10 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90 sm:h-8"
                  onClick={onMake}
                  disabled={busy || invalid > 0 || lineCount === 0}
                  aria-busy={pending || undefined}
                  aria-label={t('toBuy.makeDraftFor', { supplier: name })}
                >
                  {pending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  ) : (
                    <FilePlus2 className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {t('toBuy.makeDraft')}
                </Button>
              )
            ) : (
              <span className="text-xs text-muted-foreground">{t('toBuy.noSupplierGroup')}</span>
            )}
          </div>
          {invalid > 0 && (
            <p className="mt-1 text-xs text-destructive">{t('toBuy.invalid', { count: invalid })}</p>
          )}
        </th>
      </tr>
      {group.rows.map((row, index) => {
        const result = parsed[index];
        const rowInvalid = result !== undefined && !result.ok;
        const onOrderTotal = row.onOrder + row.inDraft;
        const inputId = `to-buy-qty-${row.skuId}`;
        return (
          <tr key={row.skuId} className="border-t align-top">
            <td className="px-3 py-2">
              <p className="break-words text-sm">{jitRowName(row)}</p>
              <p className="truncate font-mono text-[11px] text-muted-foreground">{row.sku}</p>
            </td>
            <td className="hidden break-words px-3 py-2 font-mono text-xs md:table-cell">
              {row.supplierSku ?? <span className="font-sans text-muted-foreground">-</span>}
            </td>
            <td className="px-3 py-2 text-right tabular-nums">{row.shelf}</td>
            <td className="hidden px-3 py-2 text-right tabular-nums sm:table-cell">
              {row.keepAtLeast ?? <span className="text-muted-foreground">-</span>}
            </td>
            <td className="hidden px-3 py-2 text-right tabular-nums sm:table-cell">
              {onOrderTotal}
              {row.inDraft > 0 && (
                <span className="block text-[11px] text-muted-foreground">{t('toBuy.inDraft', { count: row.inDraft })}</span>
              )}
            </td>
            <td className="px-3 py-1.5 text-right">
              {canManage && group.supplierId ? (
                <>
                  <label htmlFor={inputId} className="sr-only">
                    {t('toBuy.qtyLabel', { name: jitRowName(row) })}
                  </label>
                  <Input
                    id={inputId}
                    inputMode="numeric"
                    autoComplete="off"
                    value={textFor(row)}
                    onChange={(event) => onEdit(row.skuId, event.target.value)}
                    aria-invalid={rowInvalid || undefined}
                    className={cn('ml-auto h-10 w-20 text-right tabular-nums sm:h-8', rowInvalid && 'border-destructive')}
                  />
                </>
              ) : (
                <span className="tabular-nums">{row.toBuy}</span>
              )}
            </td>
          </tr>
        );
      })}
    </tbody>
  );
}
